import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  alpha,
  useTheme
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  ArrowForward as ArrowForwardIcon,
  HelpOutline as HelpOutlineIcon,
  People as PeopleIcon,
  PlayCircleOutline as PlayCircleIcon,
  ReceiptLong as ReceiptLongIcon,
  Sell as SellIcon,
  Storage as StorageIcon
} from '@mui/icons-material';

import {
  PARTNER_SCREEN_HELP,
  PartnerScreenId,
  PartnerSectionIcon,
  resolveLocale
} from '../../pages/partner/help/partnerHelpContent';

/**
 * AYUDA CONTEXTUAL de una pantalla del portal partner: «¿Cómo funciona esta
 * pantalla?».
 *
 * Gemelo de AccountHelpPanel del portal cliente y con el mismo reparto de
 * papeles: aquí va lo CORTO y accionable —qué es cada cifra de ESTA pantalla y
 * qué pasa si tocas cada botón—, y el enlace «saber más» lleva a la sección de
 * la guía global que desarrolla el modelo. Duplicar la prosa larga en cada
 * pantalla la habría dejado desincronizada al primer cambio de producto.
 *
 * Uso:
 *   <PartnerHelpPanel screen="billing" />                 // botón con texto
 *   <PartnerHelpPanel screen="clients" variant="icon" />  // solo el icono
 */

const ICONS: Record<PartnerSectionIcon, React.ReactElement> = {
  start: <PlayCircleIcon fontSize="small" />,
  wallet: <WalletIcon fontSize="small" />,
  clients: <PeopleIcon fontSize="small" />,
  credits: <WalletIcon fontSize="small" />,
  catalog: <SellIcon fontSize="small" />,
  storage: <StorageIcon fontSize="small" />,
  billing: <ReceiptLongIcon fontSize="small" />,
  faq: <HelpOutlineIcon fontSize="small" />
};

interface PartnerHelpPanelProps {
  screen: PartnerScreenId;
  variant?: 'button' | 'icon';
  sx?: SxProps<Theme>;
}

const PartnerHelpPanel: React.FC<PartnerHelpPanelProps> = ({
  screen,
  variant = 'button',
  sx
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const loc = resolveLocale(i18n.language);

  const help = PARTNER_SCREEN_HELP[screen];
  const copy = help[loc];

  const goToGuide = () => {
    setOpen(false);
    navigate(`/partner/ayuda#${help.section}`);
  };

  return (
    <>
      {variant === 'icon' ? (
        <Tooltip title={t('partner.help.panelButton', '¿Cómo funciona esta pantalla?') as string}>
          <IconButton size="small" onClick={() => setOpen(true)} sx={sx}>
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          size="small"
          startIcon={<HelpOutlineIcon />}
          onClick={() => setOpen(true)}
          sx={{ textTransform: 'none', ...sx }}
        >
          {t('partner.help.panelButton', '¿Cómo funciona esta pantalla?')}
        </Button>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main'
              }}
            >
              {ICONS[help.icon]}
            </Box>
            <Box>
              <Typography component="div" variant="h6" sx={{ fontWeight: 700 }}>
                {copy.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {copy.lead}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {copy.points.map((point) => (
              <Box
                component="li"
                key={point}
                sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', py: 0.85 }}
              >
                <Box
                  sx={{
                    flex: '0 0 auto',
                    mt: 0.9,
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: 'primary.main'
                  }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                  {point}
                </Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} sx={{ textTransform: 'none' }}>
            {t('common.close', 'Cerrar')}
          </Button>
          {/* «Saber más» lleva a la sección de la guía que desarrolla el modelo:
              la ayuda de pantalla resuelve el momento, la guía lo explica. */}
          <Button
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            onClick={goToGuide}
            sx={{ textTransform: 'none', boxShadow: 'none' }}
          >
            {t('partner.help.readMore', 'Saber más en la guía')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PartnerHelpPanel;
